import React, { useCallback, useContext, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, UseFormSetError } from "react-hook-form";
import * as z from "zod";
import { MultiSelect } from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { toast } from "@/components/ui/use-toast";
import { useApiErrorHandler } from "@/lib/errorHandler";
import { AuthContext, AuthContextType } from "@/context/AuthContext";
import { convertFileUrlToFileName, zodKeys } from "@/lib/utils";
import {
  ResearchAuthor,
  researchAuthorApi,
  ResearchDocument,
  researchDocumentApi,
} from "@/api/research";
import { Case, casesApi } from "@/api/cases";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { FaPaperclip } from "react-icons/fa6";
import { ImCross } from "react-icons/im";
import { useListApi } from "@/hooks/useListApi";
import { CustomToolTip } from "../../CustomToolTip";
import { useTranslation } from "react-i18next";
import { useOptimisticLocking } from "@/hooks/useOptimisticLocking";
import { ConflictResolutionDialog } from "@/components/ui/conflict-resolution-dialog";
import {
  withVersion,
  createOptimisticLockingToasts,
} from "@/lib/optimisticLockingHelpers";
import { researchDocumentFieldConfig } from "@/lib/fieldDiffUtils";

export function AddUpdateResearchDocument({
  open,
  setOpen,
  document,
  trigger,
  defaultCase,
  refresh,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  document?: ResearchDocument;
  trigger?: JSX.Element;
  defaultCase?: number;
  refresh?: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const { values: contextValues, set } = useContext(
    AuthContext,
  ) as AuthContextType;

  const { loading, user } = contextValues;
  const { setLoading } = set;

  const toasts = createOptimisticLockingToasts(
    t("research.title.research_document"),
    t,
  );

  const { loading: researchAuthorLoading, apiResult } =
    useListApi<ResearchAuthor>({
      baseApiObject: researchAuthorApi,
      defaultParams: {
        research: document?.id,
      },
      enable: open,
    });

  const author = apiResult?.results.find(
    (item) => item.research === document?.id,
  );

  const { loading: casesLoading, list: casesList } = useListApi<Case>({
    baseApiObject: casesApi,
    defaultPageSize: 1000,
    enable: open,
  });

  const FormSchema = z.object({
    title: z.string().min(3, {
      message: t("research.statictext.title_too_short"),
    }),
    description: z.string().min(3, {
      message: t("research.statictext.description_too_short"),
    }),
    file: z.instanceof(File).or(z.string()),
    private: z.boolean().default(false).optional(),
    requires_text_extraction: z.boolean().default(false).optional(),
    cases: z.string().array(),

    // Citation.
    article: z.string().nullable().optional(),
    journal_name: z.string().nullable().optional(),
    volume: z.string().nullable().optional(),
    supplement_or_issue: z.string().nullable().optional(),
    year: z.string().nullable().optional(),
    page_from: z.string().nullable().optional(),
    page_to: z.string().nullable().optional(),
    retrieved_url: z.string().nullable().optional(),

    // Author
    first_name: z.string().nullable().optional(),
    middle_name: z.string().nullable().optional(),
    last_name: z.string().nullable().optional(),
  });

  const RefinedFormSchema = FormSchema.refine(
    (data) => {
      const pageFrom = data.page_from || 0;
      const pageTo = data.page_to || 0;
      return +pageFrom <= +pageTo;
    },
    {
      message: t("research.statictext.page_to_must_be_greater"),
      path: ["page_to"], // specify the field that will be invalid
    },
  );

  const form = useForm<z.infer<typeof RefinedFormSchema>>({
    resolver: zodResolver(RefinedFormSchema),
    defaultValues: {
      title: "",
      description: "",
      file: new File([], ""),
      cases: [],
      private: false,
      requires_text_extraction: false,
      article: "",
      journal_name: "",
      volume: "",
      supplement_or_issue: "",
      year: "",
      page_from: "",
      page_to: "",
      retrieved_url: "",

      first_name: "",
      middle_name: "",
      last_name: "",
    },
  });

  const fileFieldValue = form.watch("file");

  // Function to get current form data for conflict comparison
  const getCurrentFormData = useCallback(
    (version: number): ResearchDocument => {
      const formData = form.getValues();
      return {
        id: document?.id,
        version: version,
        title: formData.title,
        description: formData.description,
        file: formData.file,
        private: formData.private || false,
        requires_text_extraction: formData.requires_text_extraction || false,
        article: formData.article,
        journal_name: formData.journal_name,
        volume: formData.volume,
        supplement_or_issue: formData.supplement_or_issue,
        year: formData.year,
        page_from: formData.page_from,
        page_to: formData.page_to,
        retrieved_url: formData.retrieved_url,
        case_ids: formData.cases.join(","),
      } as ResearchDocument;
    },
    [form, document?.id],
  );

  // Optimistic locking hook
  const {
    currentVersion,
    updateVersion,
    updateWithLocking,
    conflictState,
    handleRefresh,
    handleForceOverwrite,
    clearConflict,
  } = useOptimisticLocking<ResearchDocument>({
    initialVersion: document?.version || 0,
    resourceId: document?.id || 0,
    resourceName: t("research.title.research_document"),
    apiRead: (id) =>
      researchDocumentApi.read({
        id: id.toString(),
        token: user?.access,
      }),
    apiUpdate: (id, data) =>
      researchDocumentApi.update({
        id: id.toString() + "/",
        data,
        token: user?.access,
        headers: {
          "content-type": "multipart/form-data",
        },
      }),
    onUpdateSuccess: () => {
      toast(toasts.updateSuccess);
    },
    onConflict: () => {
      toast(toasts.conflictDetected);
    },
    onUpdateError: (error) => {
      toast(toasts.error(error));
    },
    diffConfig: researchDocumentFieldConfig,
    localData: document ? getCurrentFormData(document.version) : undefined,
  });

  const errorHandler = useApiErrorHandler("AddUpdateResearchDocument", {
    setFormError: form.setError as UseFormSetError<Record<string, unknown>>,
  });

  const onSubmit = useCallback(
    async (data: z.infer<typeof RefinedFormSchema>) => {
      try {
        setLoading?.(true);
        if (document && document.id) {
          // UPDATE with optimistic locking
          const result = await updateWithLocking(
            withVersion(
              {
                title: data.title,
                description: data.description,
                ...(typeof data.file === "string" ? {} : { file: data.file }),
                private: data.private,
                requires_text_extraction: data.requires_text_extraction,
                article: data.article,
                journal_name: data.journal_name,
                volume: data.volume,
                supplement_or_issue: data.supplement_or_issue,
                year: data.year,
                case_ids: data.cases.join(","),
                page_from: data.page_from,
                page_to: data.page_to,
                retrieved_url: data.retrieved_url,
                updated_by: user?.user.id,
              },
              currentVersion,
            ),
          );

          if (result.conflict || !result.success) return;

          if (author && author.id) {
            if (!data.first_name && !data.middle_name && !data.last_name) {
              await researchAuthorApi.delete({
                id: author.id.toString() + "/",
                data: {
                  deleted_by: user?.user.id,
                },
                token: user?.access,
              });
            } else {
              await researchAuthorApi.update({
                id: author.id.toString() + "/",
                data: {
                  first_name: data.first_name,
                  middle_name: data.middle_name,
                  last_name: data.last_name,
                  updated_by: user?.user.id,
                },
                token: user?.access,
              });
            }
          }
          // Toast already shown by optimistic locking hook
        } else {
          const research = await researchDocumentApi.create({
            url: "/",
            headers: {
              "content-type": "multipart/form-data",
            },
            data: {
              title: data.title,
              description: data.description,
              file: data.file,
              private: data.private,
              requires_text_extraction: data.requires_text_extraction,
              article: data.article,
              journal_name: data.journal_name,
              volume: data.volume,
              supplement_or_issue: data.supplement_or_issue,
              year: data.year,
              page_from: data.page_from,
              case_ids: data.cases.join(","),
              page_to: data.page_to,
              retrieved_url: data.retrieved_url,
              created_by: user?.user.id,
            },
            token: user?.access,
          });

          if (data.first_name || data.middle_name || data.last_name) {
            await researchAuthorApi.create({
              data: {
                first_name: data.first_name,
                middle_name: data.middle_name,
                last_name: data.last_name,
                created_by: user?.user.id,
                research: research.data.id,
              },
              token: user?.access,
            });
          }
          toast({
            title: t("common.toast.added_successfully", {
              resource: t("research.title.research_document"),
            }),
          });
          form.reset();
        }
        await refresh?.();
        setOpen(false);
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading?.(false);
      }
    },
    [
      currentVersion,
      updateWithLocking,
      author,
      user,
      document,
      errorHandler,
      refresh,
      setLoading,
      setOpen,
      form,
      t,
    ],
  );

  useEffect(() => {
    if (document) {
      updateVersion(document.version);
      zodKeys(FormSchema).map((field) => {
        form.setValue(
          field as keyof z.infer<typeof FormSchema>,
          {
            ...document,
            ...(author ? author : {}),
            cases: document.cases.map((item) => item.toString()),
          }[field as keyof z.infer<typeof FormSchema>],
          {
            shouldTouch: true,
          },
        );
        return undefined;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document, author]);

  useEffect(() => {
    if (defaultCase !== undefined && open) {
      form.setValue("cases", [defaultCase.toString()], { shouldTouch: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCase, open]);

  const buttonAction = document?.id
    ? t("common.button.update")
    : t("common.button.add");

  // Conflict resolution handlers
  const handleRefreshDocument = async () => {
    if (!document?.id) return;
    const refreshed = await handleRefresh();
    if (refreshed) {
      // Update form with refreshed data
      updateVersion(refreshed.version);
      zodKeys(FormSchema).map((field) => {
        form.setValue(
          field as keyof z.infer<typeof FormSchema>,
          {
            ...refreshed,
            ...(author ? author : {}),
            cases: refreshed.cases.map((item) => item.toString()),
          }[field as keyof z.infer<typeof FormSchema>],
          {
            shouldTouch: true,
          },
        );
        return undefined;
      });
      toast(toasts.refreshSuccess);
    }
  };

  const handleForceOverwriteDocument = async () => {
    if (!conflictState.latestVersion || !document?.id) return;
    const formData = form.getValues();
    const result = await handleForceOverwrite(
      withVersion(
        {
          title: formData.title,
          description: formData.description,
          ...(typeof formData.file === "string" ? {} : { file: formData.file }),
          private: formData.private,
          requires_text_extraction: formData.requires_text_extraction,
          article: formData.article,
          journal_name: formData.journal_name,
          volume: formData.volume,
          supplement_or_issue: formData.supplement_or_issue,
          year: formData.year,
          case_ids: formData.cases.join(","),
          page_from: formData.page_from,
          page_to: formData.page_to,
          retrieved_url: formData.retrieved_url,
          updated_by: user?.user.id,
        },
        conflictState.latestVersion,
      ),
    );
    if (result.success) {
      toast(toasts.overwriteSuccess);
      await refresh?.();
      setOpen(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        {!!trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
        <DialogContent
          className="min-w-[70%] overflow-scroll overflow-x-hidden h-full"
          onInteractOutside={(e) => {
            e.preventDefault();
          }}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="w-full flex flex-col gap-8"
              onKeyDown={(e) => {
                if (e.key === "Enter") e.preventDefault();
              }}
            >
              <DialogHeader>
                <DialogTitle className="text-2xl">
                  {buttonAction} {t("research.title.research_document")}
                </DialogTitle>
              </DialogHeader>

              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>* {t("common.label.title")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("common.placeholder.title")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="private"
                render={({ field }) => (
                  <div>
                    <FormItem className="flex flex-row items-center gap-2">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormLabel className="!mt-0">
                        {t("common.label.private")}
                      </FormLabel>
                      <FormMessage />
                    </FormItem>
                    <FormLabel className="text-slate-400">
                      {t("research.statictext.private")}
                    </FormLabel>
                  </div>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>* {t("common.label.description")}</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder={t("common.placeholder.description")}
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="file"
                render={({ field }) => {
                  let fileName = null;
                  if (typeof field.value === "string") {
                    fileName = convertFileUrlToFileName(field.value);
                  }
                  return (
                    <FormItem>
                      <FormLabel>* {t("common.label.file")}</FormLabel>
                      {fileName && (
                        <div className="flex gap-2 items-center">
                          <FaPaperclip color="skyblue" />
                          <a
                            href={field.value as string}
                            target="_blank"
                            rel="noreferrer"
                            className="underline text-primary break-all"
                          >
                            {fileName}
                          </a>
                          <CustomToolTip
                            tip={t("common.statictext.remove_file")}
                            trigger={
                              <ImCross
                                className="cursor-pointer"
                                onClick={() => {
                                  form.setValue("file", new File([], ""));
                                }}
                                color="red"
                              />
                            }
                          />
                        </div>
                      )}
                      <FormControl>
                        <Input
                          type="file"
                          {...field}
                          value={undefined}
                          onChange={(event) =>
                            form.setValue(
                              "file",
                              event.target.files
                                ? event.target.files[0]
                                : new File([], ""),
                            )
                          }
                        />
                      </FormControl>
                      <FormDescription>
                        {t("common.statictext.upload_files")}
                        <p className="text-sm text-info">
                          {t("common.statictext.upload_files_detail")}
                        </p>
                        {document?.ocr_task_status && (
                          <p>
                            {t("common.label.ocr_status")}
                            {": "}
                            <b>{document?.ocr_task_status}</b>
                            {document.ocr_task_status_message && (
                              <span> ({document.ocr_task_status_message})</span>
                            )}
                          </p>
                        )}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />

              {fileFieldValue && typeof fileFieldValue !== "string" && (
                <FormField
                  control={form.control}
                  name="requires_text_extraction"
                  render={({ field }) => (
                    <div>
                      <FormItem className="flex flex-row items-center gap-2">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <FormLabel className="!mt-0">
                          {t("common.label.requires_text_extraction")}
                        </FormLabel>
                        <FormMessage />
                      </FormItem>
                      <FormDescription className="mt-1">
                        {t("research.statictext.requires_text_extraction")}
                      </FormDescription>
                      <FormDescription>
                        <p>{t("common.statictext.ocr_operations_email")}</p>
                        <p>
                          {t("common.statictext.update_email_preferences")}{" "}
                          <a
                            target="_blank"
                            href="/email-settings"
                            className="text-primary underline"
                          >
                            {t("common.statictext.here")}.
                          </a>
                        </p>
                      </FormDescription>
                    </div>
                  )}
                />
              )}

              {!defaultCase && (
                <FormField
                  control={form.control}
                  name="cases"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("common.label.case")}{" "}
                        {casesLoading
                          ? t("common.button.loading_with_parens")
                          : ""}
                      </FormLabel>
                      <MultiSelect
                        selected={field.value}
                        options={
                          casesList.map((item) => ({
                            label: item.name,
                            value: item.id.toString(),
                          })) || []
                        }
                        {...field}
                        className="sm:w-[510px]"
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              <h3 className="text-2xl mt-8">{t("research.label.citation")}</h3>
              <hr />
              <div className="flex flex-row gap-4 items-center">
                <FormLabel>{t("research.label.citation_author")}</FormLabel>
                <FormField
                  control={form.control}
                  name="first_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("common.label.first_name")}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t("common.placeholder.first_name")}
                          {...field}
                          value={field.value || undefined}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="middle_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("common.label.middle_name")}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t("common.placeholder.middle_name")}
                          {...field}
                          value={field.value || undefined}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="last_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("common.placeholder.last_name")}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t("common.placeholder.last_name")}
                          {...field}
                          value={field.value || undefined}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="article"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("research.label.article")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("research.placeholder.article")}
                        {...field}
                        value={field.value || undefined}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="journal_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("research.label.journal_name")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("research.placeholder.journal_name")}
                        {...field}
                        value={field.value || undefined}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="volume"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("research.label.volume")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("research.placeholder.volume")}
                        {...field}
                        value={field.value || undefined}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="supplement_or_issue"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("research.label.supplement_or_issue")}
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t(
                          "research.placeholder.supplement_or_issue",
                        )}
                        {...field}
                        value={field.value || undefined}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="year"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("research.label.year")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("research.placeholder.year")}
                        {...field}
                        value={field.value || undefined}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="page_from"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("common.label.start_page")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("research.placeholder.start_page")}
                        {...field}
                        value={field.value || undefined}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="page_to"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("common.label.end_page")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("research.placeholder.end_page")}
                        {...field}
                        value={field.value || undefined}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="retrieved_url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("research.label.retrieved_url")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("research.placeholder.retrieved_url")}
                        {...field}
                        value={field.value || undefined}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button
                  disabled={loading || researchAuthorLoading}
                  type="submit"
                >
                  {loading || researchAuthorLoading
                    ? t("common.button.loading")
                    : buttonAction}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Conflict Resolution Dialog */}
      <ConflictResolutionDialog
        open={conflictState.isOpen}
        onOpenChange={(open) => !open && clearConflict()}
        currentVersion={currentVersion}
        latestVersion={conflictState.latestVersion}
        resourceName={t("research.title.research_document")}
        onRefresh={handleRefreshDocument}
        onOverwrite={handleForceOverwriteDocument}
        loading={loading}
        fieldDiffs={conflictState.fieldDiffs}
        fieldLabels={Object.fromEntries(
          Object.entries(researchDocumentFieldConfig.fields).map(
            ([field, config]) => [field, t(config.label)],
          ),
        )}
      />
    </>
  );
}
